import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import {
  QUALITY_ORDER,
  QUALITY_PRESETS,
  type ContentHintMode,
  type QualityId,
} from "@shared/protocol";
import type { VoiceState } from "@shared/types";
import { C, FONT_MONO, hexA, initialOf } from "@/theme";
import type { LocalStreams } from "@/media/types";
import type { RemoteMedia } from "@/state/appTypes";
import {
  activeVoiceChannel,
  can,
  colorOf,
  connectedVoiceChannel,
  participantsOf,
  useApp,
  userById,
} from "@/state/store";
import {
  CamIcon,
  HeadphonesIcon,
  MicIcon,
  PowerIcon,
  ScreenIcon,
  SpeakerIcon,
} from "@/components/icons";
import { VideoTile } from "@/components/VideoTile";
import { ShareScreenModal } from "@/components/ShareScreenModal";
import { abrirJanelaDeTela, type JanelaDeTela } from "@/media/janelaDeTela";

const NO_MEDIA: RemoteMedia = { mic: null, cam: null, screen: null };

/**
 * Fica no lugar do video quando quem transmite ESCOLHE nao ver a propria tela.
 *
 * Ver a propria transmissao e o normal — e a unica forma de conferir que esta
 * indo o que se pretende mandar, e nao a janela errada ou uma tela ja trocada.
 * O que existia antes era este aviso e mais nada, entao quem transmitia
 * transmitia no escuro.
 *
 * O motivo de existir a opcao de esconder e o espelho: quando a captura e a
 * tela INTEIRA e o LFRCORD esta nela, o preview aparece dentro de si mesmo,
 * repetido ate o infinito. Nao quebra nada — a codificacao manda a tela do
 * mesmo jeito —, mas cansa de olhar. Quem se incomoda desliga aqui e o resto
 * da sala continua vendo normalmente.
 */
function SelfShareNotice({ accent }: { accent: string }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
        padding: 24,
        textAlign: "center",
      }}
    >
      <span
        style={{
          width: 52,
          height: 52,
          display: "grid",
          placeItems: "center",
          borderRadius: 14,
          background: hexA(accent, 0.14),
          color: accent,
        }}
      >
        <ScreenIcon size={22} />
      </span>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.textStrong }}>
          Você está transmitindo sua tela
        </div>
        <div style={{ marginTop: 4, fontSize: 12, color: C.textDim, maxWidth: 280 }}>
          A prévia está escondida. Os outros membros continuam vendo ao vivo — use
          &ldquo;Ver minha tela&rdquo; para conferir o que está indo.
        </div>
      </div>
    </div>
  );
}

/**
 * Botao de canto do tile de transmissao.
 *
 * Fora do componente de proposito: definido la dentro, ele seria um tipo novo
 * a cada render e o React desmontaria e remontaria o botao junto — um clique
 * que caisse no meio de um render se perderia.
 *
 * `stopPropagation` porque o tile inteiro e clicavel: sem isso, "Ocultar
 * previa" abriria a tela cheia por baixo.
 */
function TileAcao({ rotulo, onClick }: { rotulo: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className="hov-bg-26"
      onClick={event => {
        event.stopPropagation();
        onClick();
      }}
      style={{
        position: "absolute",
        right: 10,
        bottom: 10,
        height: 26,
        padding: "0 11px",
        borderRadius: 999,
        cursor: "pointer",
        border: `1px solid ${C.lineStrong}`,
        background: "rgba(0,0,0,.55)",
        color: C.textSoft,
        fontFamily: FONT_MONO,
        fontSize: 10.5,
        letterSpacing: ".04em",
      }}
    >
      {rotulo}
    </button>
  );
}

const CONTENT_MODES: { id: ContentHintMode; label: string; title: string }[] = [
  { id: "motion", label: "Movimento", title: "Prioriza quadros por segundo (jogos)" },
  { id: "detail", label: "Nitidez", title: "Prioriza nitidez (texto e código)" },
];

const TALK_MODES: { on: boolean; label: string; title: string }[] = [
  {
    on: false,
    label: "Sempre aberto",
    title: "O microfone fica aberto enquanto você não estiver mudo.",
  },
  {
    on: true,
    label: "Apertar para falar",
    title: "O microfone só abre enquanto a barra de espaço estiver pressionada.",
  },
];

const SINK_UNSUPPORTED =
  "Este navegador não permite escolher a saída de áudio; o som usa o dispositivo padrão do sistema.";

const SINK_FAILED =
  "Não foi possível usar a saída de áudio escolhida; o som continua no dispositivo padrão.";

/** Sem permissão o navegador devolve entradas de id vazio, que não dá para escolher. */
function usableDevices(list: MediaDeviceInfo[]): MediaDeviceInfo[] {
  return list.filter(device => device.deviceId !== "");
}

/** `setSinkId` só existe no Chrome e derivados; nos outros a saída fica travada no padrão. */
function sinkSupported(): boolean {
  if (typeof HTMLMediaElement === "undefined") return false;
  return typeof HTMLMediaElement.prototype.setSinkId === "function";
}

const pillStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  height: 38,
  padding: "0 6px 0 13px",
  border: `1px solid ${C.lineStrong}`,
  borderRadius: 999,
  background: C.raised,
};

const pillLabelStyle: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 9.5,
  fontWeight: 600,
  letterSpacing: ".1em",
  color: C.textLabel,
};

const selectStyle: CSSProperties = {
  height: 28,
  padding: "0 8px",
  border: 0,
  borderRadius: 999,
  background: C.field,
  color: C.textStrong,
  fontSize: 12,
  outline: 0,
  cursor: "pointer",
};

/** Trilha remota tocada fora do <video>: respeita o botao de ensurdecer, o volume e a saida. */
function HiddenAudio({
  stream,
  deafened,
  volume,
  sinkId,
  onSinkError,
}: {
  stream: MediaStream;
  deafened: boolean;
  volume: number;
  sinkId: string;
  onSinkError: () => void;
}) {
  const ref = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (element.srcObject !== stream) element.srcObject = stream;
    // `muted` sozinho nao basta pra garantir que nada chega na captura de
    // audio do sistema — depende de detalhe interno do navegador sobre como
    // uma trilha muda e roteada. `pause()` e inequivoco: elemento pausado
    // nao decodifica nem entrega nenhum quadro de audio pra lugar nenhum.
    if (deafened) {
      element.pause();
    } else {
      element.play().then(
        () => undefined,
        () => undefined,
      );
    }
  }, [deafened, stream]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    element.volume = Math.min(1, Math.max(0, volume));
  }, [volume]);

  // `setSinkId("")` volta para a saida padrao do sistema; so o pedido de um
  // aparelho especifico vira aviso quando falha.
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (typeof element.setSinkId !== "function") return;
    element.setSinkId(sinkId).then(
      () => undefined,
      () => {
        if (sinkId) onSinkError();
      },
    );
  }, [onSinkError, sinkId]);

  return <audio ref={ref} autoPlay muted={deafened} style={{ display: "none" }} />;
}

/** Deslizante de volume do membro, exibido no hover do tile. So vale para esta aba. */
/** Icone, slider e porcentagem — sem posicionamento, para os dois lugares que usam volume. */
function VolumeSliderContent({
  name,
  value,
  accent,
  onChange,
}: {
  name: string;
  value: number;
  accent: string;
  onChange: (volume: number) => void;
}) {
  const percent = Math.round(Math.min(1, Math.max(0, value)) * 100);
  return (
    <>
      <SpeakerIcon size={12} color={percent === 0 ? C.red : C.textSoft} />
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={percent}
        onChange={event => onChange(Number(event.target.value) / 100)}
        title={`Volume de ${name}`}
        aria-label={`Volume de ${name}`}
        style={{
          width: 92,
          height: 4,
          margin: 0,
          accentColor: accent,
          background: "transparent",
          cursor: "pointer",
        }}
      />
      <span
        style={{
          width: 32,
          textAlign: "right",
          fontFamily: FONT_MONO,
          fontSize: 9.5,
          color: C.textSoft,
        }}
      >
        {percent}%
      </span>
    </>
  );
}

/** Controle de volume flutuante, ancorado no canto do tile — aparece no hover. */
function VolumeControl(props: { name: string; value: number; accent: string; onChange: (volume: number) => void }) {
  return (
    <span
      onClick={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") event.stopPropagation();
      }}
      style={{
        position: "absolute",
        right: 10,
        bottom: 10,
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "4px 9px",
        borderRadius: 7,
        background: "rgba(0,0,0,.5)",
        animation: "fadein 120ms ease",
      }}
    >
      <VolumeSliderContent {...props} />
    </span>
  );
}

/** Marca de quem foi silenciado por um moderador — diferente do mic cortado do proprio membro. */
function ServerMutedChip() {
  return (
    <span
      title="Silenciado pela moderação"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "1px 6px",
        borderRadius: 5,
        border: `1px solid ${hexA(C.red, 0.45)}`,
        background: hexA(C.red, 0.18),
        fontFamily: FONT_MONO,
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: ".08em",
        color: C.red,
      }}
    >
      <MicIcon size={9} color={C.red} bare />
      SILENCIADO
    </span>
  );
}

function DeviceSelect({
  label,
  devices,
  value,
  fallbackName,
  disabled,
  title,
  onSelect,
}: {
  label: string;
  devices: MediaDeviceInfo[];
  value: string;
  fallbackName: string;
  disabled: boolean;
  title: string;
  onSelect: (deviceId: string) => void;
}) {
  const empty = devices.length === 0;
  return (
    <div style={pillStyle}>
      <span style={pillLabelStyle}>{label}</span>
      <select
        value={value}
        disabled={disabled || empty}
        title={title}
        onChange={(event: ChangeEvent<HTMLSelectElement>) => onSelect(event.target.value)}
        style={{
          ...selectStyle,
          maxWidth: 172,
          opacity: disabled || empty ? 0.5 : 1,
          cursor: disabled || empty ? "not-allowed" : "pointer",
        }}
      >
        {empty && <option value="">Nenhum dispositivo encontrado</option>}
        {!empty && <option value="">Padrão do sistema</option>}
        {devices.map((device, index) => (
          <option key={device.deviceId} value={device.deviceId}>
            {device.label || `${fallbackName} ${index + 1}`}
          </option>
        ))}
      </select>
    </div>
  );
}

export function VoiceView() {
  const { state, actions } = useApp();
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const [hoverTile, setHoverTile] = useState<string | null>(null);
  const [brokenAvatars, setBrokenAvatars] = useState<Record<string, true>>({});
  const [foundInputs, setFoundInputs] = useState<MediaDeviceInfo[]>([]);
  const [foundOutputs, setFoundOutputs] = useState<MediaDeviceInfo[]>([]);
  const [sinkError, setSinkError] = useState<string | null>(null);
  /** Ver a propria transmissao. Ligado por padrao — ver o que se manda e o normal. */
  const [previaPropria, setPreviaPropria] = useState(true);
  /**
   * Janelas de transmissao abertas, por `peerId`.
   *
   * Num `ref` porque sao objetos do navegador, nao dados de tela: recriar a
   * cada render fecharia e reabriria a janela na cara de quem esta assistindo.
   * O `useState` ao lado existe so para a tela saber quem esta com janela
   * aberta e trocar o rotulo do tile.
   */
  const janelas = useRef(new Map<string, JanelaDeTela>());
  const [emJanela, setEmJanela] = useState<string[]>([]);

  const viewed = activeVoiceChannel(state) ?? connectedVoiceChannel(state);
  const viewedId = viewed?.id ?? "";
  const participants = viewedId ? participantsOf(state, viewedId) : [];
  const connected = viewedId !== "" && state.voiceId === viewedId;
  const accent = state.settings.accent;
  const canOutput = sinkSupported();

  const mediaOf = (peerId: string): LocalStreams | RemoteMedia =>
    peerId === state.peerId ? state.local : state.remote[peerId] ?? NO_MEDIA;

  const nameOf = (userId: string): string => userById(state, userId)?.name ?? "Membro";

  const avatarOf = (userId: string): string | null => {
    const url = userById(state, userId)?.avatarUrl ?? null;
    if (!url || brokenAvatars[url]) return null;
    return url;
  };

  const focusPeer = state.focus;
  const focusEntry = focusPeer
    ? participants.find(item => item.peerId === focusPeer && item.sharing)
    : undefined;
  const focusIsSelf = focusEntry?.peerId === state.peerId;
  // A propria tela em tela cheia agora mostra a tela, nao um aviso. Quem
  // desligou a previa continua vendo o aviso — foi escolha dele.
  const focusStream = focusEntry
    ? focusIsSelf
      ? previaPropria
        ? state.local.screen
        : null
      : mediaOf(focusEntry.peerId).screen
    : null;
  const focusOpen = Boolean(focusEntry);

  const closeFocus = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().then(
        () => undefined,
        () => undefined,
      );
    }
    actions.setFocus(null);
  }, [actions]);

  /** "Sair da tela cheia" derruba primeiro a tela cheia nativa, se houver. */
  const leaveFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().then(
        () => undefined,
        () => undefined,
      );
      return;
    }
    actions.setFocus(null);
  }, [actions]);

  const handleSinkError = useCallback(() => setSinkError(SINK_FAILED), []);

  /* ------------------------------------------------------------------ */
  /* Assistir numa janela separada                                       */
  /* ------------------------------------------------------------------ */

  const fecharJanela = useCallback((peerId: string) => {
    janelas.current.get(peerId)?.fechar();
    janelas.current.delete(peerId);
    setEmJanela(atual => atual.filter(id => id !== peerId));
  }, []);

  /**
   * Abre (ou traz para a frente) a janela de quem está transmitindo.
   *
   * Quando o navegador recusa o pop-up — e é ele quem decide —, cai na tela
   * cheia de dentro do app. Um clique em "assistir" que não faz nada seria
   * pior do que a tela cheia antiga.
   */
  const assistirEmJanela = useCallback(
    (peerId: string, titulo: string, stream: MediaStream | null) => {
      const jaAberta = janelas.current.get(peerId);
      if (jaAberta?.aberta()) {
        jaAberta.focar();
        jaAberta.atualizar(stream);
        return;
      }

      const janela = abrirJanelaDeTela({
        peerId,
        titulo,
        stream,
        aoFechar: () => {
          janelas.current.delete(peerId);
          setEmJanela(atual => atual.filter(id => id !== peerId));
        },
      });

      if (!janela) {
        actions.setFocus(peerId);
        return;
      }
      janelas.current.set(peerId, janela);
      setEmJanela(atual => (atual.includes(peerId) ? atual : [...atual, peerId]));
    },
    [actions],
  );

  /*
    Mantém as janelas abertas em dia com o que está chegando.

    Duas coisas mudam por fora delas: a trilha, quando a pessoa reinicia a
    captura (o `MediaStream` é outro objeto), e a própria transmissão, quando
    ela para. Uma janela pendurada num objeto morto ficaria congelada no
    último quadro, parecendo que a transmissão continua.
  */
  useEffect(() => {
    for (const [peerId, janela] of janelas.current) {
      const aindaTransmite = participants.some(item => item.peerId === peerId && item.sharing);
      if (!aindaTransmite) {
        janela.fechar();
        continue;
      }
      janela.atualizar(
        peerId === state.peerId ? state.local.screen : state.remote[peerId]?.screen ?? null,
      );
    }
  }, [participants, state.local.screen, state.peerId, state.remote]);

  /* Sair da sala (ou fechar a tela) não pode deixar janela órfã. */
  useEffect(() => {
    const abertas = janelas.current;
    return () => {
      for (const janela of abertas.values()) janela.fechar();
      abertas.clear();
    };
  }, []);

  useEffect(() => {
    if (focusPeer && !focusOpen) actions.setFocus(null);
  }, [actions, focusOpen, focusPeer]);

  useEffect(() => {
    if (!focusOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeFocus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeFocus, focusOpen]);

  /** A janela exclusiva tenta a tela cheia real; falha em silencio se o navegador negar. */
  useEffect(() => {
    if (!focusOpen) return;
    const element = overlayRef.current;
    if (!element || document.fullscreenElement) return;
    if (typeof element.requestFullscreen !== "function") return;
    element.requestFullscreen().then(
      () => undefined,
      () => undefined,
    );
  }, [focusOpen]);

  /**
   * Lista os dispositivos de audio. Os rotulos so aparecem depois que o
   * navegador libera o microfone, por isso a leitura se repete ao conectar.
   */
  useEffect(() => {
    const media: MediaDevices | undefined = navigator.mediaDevices;
    if (!media || typeof media.enumerateDevices !== "function") return;
    let alive = true;
    const read = () => {
      media.enumerateDevices().then(
        list => {
          if (!alive) return;
          setFoundInputs(list.filter(device => device.kind === "audioinput"));
          setFoundOutputs(list.filter(device => device.kind === "audiooutput"));
        },
        () => undefined,
      );
    };
    read();
    media.addEventListener("devicechange", read);
    return () => {
      alive = false;
      media.removeEventListener("devicechange", read);
    };
  }, [state.voiceId, state.local.mic]);

  /**
   * A lista do estado manda; a leitura local cobre o caso de ela ainda nao ter
   * dispositivos utilizaveis. A comparacao usa as listas ja filtradas: antes da
   * permissao o navegador devolve entradas de `deviceId` vazio, que nao servem
   * de opcao e nao podem esconder a leitura feita depois do microfone abrir.
   */
  const inputs = useMemo(() => {
    const fromState = usableDevices(state.devices.inputs);
    return fromState.length > 0 ? fromState : usableDevices(foundInputs);
  }, [foundInputs, state.devices.inputs]);

  const outputs = useMemo(() => {
    const fromState = usableDevices(state.devices.outputs);
    return fromState.length > 0 ? fromState : usableDevices(foundOutputs);
  }, [foundOutputs, state.devices.outputs]);

  // O estado e a unica fonte: se a captura recusar o aparelho escolhido, ele
  // volta para nulo la e o seletor precisa acompanhar essa volta.
  const inputId = state.devices.inputId ?? "";
  const outputId = state.devices.outputId ?? "";
  const inputValue = inputs.some(device => device.deviceId === inputId) ? inputId : "";
  const outputValue = outputs.some(device => device.deviceId === outputId) ? outputId : "";

  const onOutputDevice = (deviceId: string) => {
    setSinkError(null);
    actions.setOutputDevice(deviceId);
  };

  const onQuality = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    const next = QUALITY_ORDER.find((id: QualityId) => id === value);
    if (next) actions.setShareQuality(next);
  };

  if (!viewed) return null;

  const canShare = can(state, "tela");
  const canVoice = can(state, "voz");
  // No navegador (sem app desktop) a captura de audio do sistema pega tudo,
  // inclusive a propria call — so da pra evitar o eco ensurdecendo local pra
  // quem esta transmitindo, so enquanto o audio do sistema estiver incluso.
  const webLocalMuteForCapture =
    !Boolean(window.lfrcordDesktop) && state.sharing && state.shareSystemAudio;
  const offBg = C.lineTile;
  const offFg = C.textStrong;
  const redBg = hexA(C.red, 0.16);
  const accentBg = hexA(accent, 0.2);
  const mutedBorder = hexA(C.red, 0.45);
  const screens = participants.filter(item => item.sharing);
  const meServerMuted = state.me?.serverMuted ?? false;
  /**
   * O audio segue a sala em que estamos conectados, nao a que esta sendo vista:
   * da para abrir outra sala de voz sem perder o som da sua.
   */
  const audioPeers = state.voiceId
    ? participantsOf(state, state.voiceId).filter(item => item.peerId !== state.peerId)
    : [];

  const volumeOf = (userId: string): number => state.volumes[userId] ?? 1;

  const controlStyle = (on: boolean, onBg: string, tone: string) => ({
    width: 42,
    height: 42,
    display: "grid" as const,
    placeItems: "center" as const,
    border: 0,
    borderRadius: 999,
    cursor: "pointer",
    background: on ? onBg : offBg,
    color: on ? tone : offFg,
  });

  /**
   * Handlers de hover/foco que revelam o volume do tile. Enquanto o deslizante
   * estiver em foco — ou seja, enquanto o membro arrasta —, sair com o mouse
   * nao esconde o controle: sumir no meio do arrasto cancelaria o ajuste.
   */
  const hoverProps = (key: string) => ({
    onMouseEnter: () => setHoverTile(key),
    onMouseLeave: (event: ReactMouseEvent<HTMLDivElement>) => {
      if (event.currentTarget.contains(document.activeElement)) return;
      setHoverTile(prev => (prev === key ? null : prev));
    },
    onFocus: () => setHoverTile(key),
    onBlur: (event: ReactFocusEvent<HTMLDivElement>) => {
      if (event.currentTarget.contains(event.relatedTarget)) return;
      setHoverTile(prev => (prev === key ? null : prev));
    },
  });

  const volumeNodeFor = (item: VoiceState, key: string): ReactNode => {
    if (!connected || item.peerId === state.peerId) return null;
    if (hoverTile !== key) return null;
    return (
      <VolumeControl
        name={nameOf(item.userId)}
        value={volumeOf(item.userId)}
        accent={accent}
        onChange={volume => actions.setVolume(item.userId, volume)}
      />
    );
  };

  const renderTile = (item: VoiceState) => {
    const media = mediaOf(item.peerId);
    const isSelf = item.peerId === state.peerId;
    const color = colorOf(state, item.userId);
    const name = nameOf(item.userId) + (isSelf ? " (você)" : "");
    const speaking =
      (state.speaking[item.peerId] ?? item.speaking) && !(state.deafened || webLocalMuteForCapture);
    const ring = speaking ? "spk 1.6s ease-in-out infinite" : "none";
    const serverMuted = userById(state, item.userId)?.serverMuted ?? false;
    const avatar = avatarOf(item.userId);
    const badge = (
      <>
        {item.muted && !serverMuted && <MicIcon size={11} color={C.red} bare />}
        {serverMuted && <ServerMutedChip />}
      </>
    );
    const volume = volumeNodeFor(item, item.peerId);

    const onContextMenu = isSelf
      ? undefined
      : (event: ReactMouseEvent<HTMLDivElement>) => {
          event.preventDefault();
          event.stopPropagation();
          actions.openContextMenu(item.peerId, item.userId, event.clientX, event.clientY);
        };

    if (item.cam && media.cam) {
      return (
        <div
          key={item.peerId}
          {...hoverProps(item.peerId)}
          onContextMenu={onContextMenu}
          style={{
            position: "relative",
            borderRadius: 14,
            animation: speaking ? "spk 1.6s ease-in-out infinite" : undefined,
          }}
        >
          <VideoTile
            stream={media.cam}
            label={name}
            kind="cam"
            stats={state.stats[item.peerId]?.cam}
            muted
            mirrored={isSelf}
            labelBadge={badge}
            overlay={volume}
            borderColor={serverMuted ? mutedBorder : undefined}
          />
        </div>
      );
    }

    return (
      <div
        key={item.peerId}
        {...hoverProps(item.peerId)}
        onContextMenu={onContextMenu}
        style={{
          aspectRatio: "16/10",
          position: "relative",
          borderRadius: 14,
          border: `1px solid ${serverMuted ? mutedBorder : C.lineTile}`,
          background: C.tile,
          display: "grid",
          placeItems: "center",
          animation: "pop 220ms ease",
        }}
      >
        <span
          style={{
            width: 64,
            height: 64,
            display: "grid",
            placeItems: "center",
            borderRadius: "50%",
            fontSize: 24,
            fontWeight: 700,
            background: hexA(color, 0.18),
            color,
            animation: ring,
          }}
        />
        {avatar ? (
          <img
            src={avatar}
            alt=""
            onError={() =>
              setBrokenAvatars(prev => (prev[avatar] ? prev : { ...prev, [avatar]: true }))
            }
            style={{
              position: "absolute",
              width: 64,
              height: 64,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
        ) : (
          <span
            style={{
              position: "absolute",
              width: 64,
              height: 64,
              display: "grid",
              placeItems: "center",
              borderRadius: "50%",
              fontSize: 24,
              fontWeight: 700,
              color,
            }}
          >
            {initialOf(nameOf(item.userId))}
          </span>
        )}
        <span
          style={{
            position: "absolute",
            left: 10,
            bottom: 10,
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 9px",
            borderRadius: 7,
            background: "rgba(0,0,0,.5)",
            fontFamily: FONT_MONO,
            fontSize: 10.5,
            color: C.textStrong,
          }}
        >
          {name}
          {badge}
        </span>
        {volume}
      </div>
    );
  };

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        background: C.stage,
      }}
    >
      <div
        style={{
          height: 54,
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "0 16px",
          borderBottom: `1px solid ${C.line}`,
        }}
      >
        <SpeakerIcon size={18} color={C.textFaint} />
        <span style={{ fontSize: 15, fontWeight: 700 }}>{viewed.name}</span>
        <span style={{ width: 1, height: 18, background: C.lineStrong }} />
        <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, color: C.green }}>
          {participants.length} CONECTADOS
        </span>
        <span style={{ flex: 1 }} />
        <button
          className="hov-bg-26"
          onClick={actions.backToText}
          style={{
            height: 30,
            padding: "0 12px",
            border: `1px solid ${C.lineStrong}`,
            borderRadius: 8,
            cursor: "pointer",
            background: "transparent",
            color: C.textSoft,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          Voltar ao chat
        </button>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 24,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))",
            gap: 14,
            alignContent: "center",
            maxWidth: 980,
            width: "100%",
            margin: "0 auto",
          }}
        >
          {screens.map(item => {
            const key = `screen:${item.peerId}`;
            const isSelf = item.peerId === state.peerId;
            return (
              <div
                key={key}
                {...hoverProps(key)}
                onContextMenu={
                  isSelf
                    ? undefined
                    : event => {
                        event.preventDefault();
                        event.stopPropagation();
                        actions.openContextMenu(key, item.userId, event.clientX, event.clientY);
                      }
                }
                style={{
                  // Uma unica transmissao merece destaque (linha inteira); com
                  // duas ou mais ao mesmo tempo, forcar largura total empilha
                  // tiles enormes um embaixo do outro — melhor deixar fluir no
                  // mesmo grid responsivo das cameras.
                  gridColumn: screens.length === 1 ? "1/-1" : undefined,
                  position: "relative",
                }}
              >
                <VideoTile
                  // Quem transmite ve a propria tela. So nao ve se pediu para
                  // esconder — o espelho da captura de tela inteira incomoda
                  // alguns, e essa parte segue sendo escolha de quem transmite.
                  stream={
                    isSelf
                      ? previaPropria
                        ? state.local.screen
                        : null
                      : mediaOf(item.peerId).screen
                  }
                  label={isSelf ? "Sua transmissão" : `Tela de ${nameOf(item.userId)}`}
                  kind="screen"
                  stats={state.stats[item.peerId]?.screen}
                  live
                  muted
                  // Clicar na tela de outra pessoa abre uma JANELA. Assistir
                  // deixou de custar o app inteiro: da para ler o canal, falar
                  // e olhar quem esta na sala com a transmissao do lado ou no
                  // outro monitor. A propria tela continua abrindo aqui
                  // dentro — nao ha nada de que ela precise ficar ao lado.
                  onFocus={
                    isSelf
                      ? () => actions.setFocus(item.peerId)
                      : () =>
                          assistirEmJanela(
                            item.peerId,
                            `Tela de ${nameOf(item.userId)}`,
                            mediaOf(item.peerId).screen,
                          )
                  }
                  overlay={
                    <>
                      {volumeNodeFor(item, key)}
                      <TileAcao
                        rotulo={
                          isSelf
                            ? previaPropria
                              ? "Ocultar prévia"
                              : "Ver minha tela"
                            : emJanela.includes(item.peerId)
                              ? "Fechar janela"
                              : "Assistir em janela"
                        }
                        onClick={() => {
                          if (isSelf) {
                            setPreviaPropria(atual => !atual);
                            return;
                          }
                          if (emJanela.includes(item.peerId)) {
                            fecharJanela(item.peerId);
                            return;
                          }
                          assistirEmJanela(
                            item.peerId,
                            `Tela de ${nameOf(item.userId)}`,
                            mediaOf(item.peerId).screen,
                          );
                        }}
                      />
                    </>
                  }
                  emptyState={
                    isSelf && !previaPropria ? <SelfShareNotice accent={accent} /> : undefined
                  }
                />
              </div>
            );
          })}

          {participants.map(renderTile)}

          {participants.length === 0 && (
            <div
              style={{
                gridColumn: "1/-1",
                display: "grid",
                placeItems: "center",
                padding: "48px 0",
                fontFamily: FONT_MONO,
                fontSize: 11,
                letterSpacing: ".12em",
                color: C.textFaint,
              }}
            >
              SALA VAZIA
            </div>
          )}
        </div>

        <div
          style={{
            flex: "0 0 auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            paddingTop: 22,
          }}
        >
          {connected && meServerMuted && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 14px",
                borderRadius: 999,
                border: `1px solid ${mutedBorder}`,
                background: hexA(C.red, 0.14),
                fontFamily: FONT_MONO,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: ".1em",
                color: C.red,
              }}
            >
              <MicIcon size={11} color={C.red} bare />
              VOCÊ ESTÁ SILENCIADO PELA MODERAÇÃO
            </div>
          )}

          {connected ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: 8,
                border: `1px solid ${C.lineStrong}`,
                borderRadius: 999,
                background: C.raised,
              }}
            >
              <button
                className="hov-bright"
                onClick={actions.toggleMute}
                title={
                  meServerMuted
                    ? "Você foi silenciado pela moderação e não consegue transmitir voz."
                    : "Microfone"
                }
                style={controlStyle(state.muted || meServerMuted, redBg, C.red)}
              >
                <MicIcon
                  size={18}
                  color={state.muted || meServerMuted ? C.red : offFg}
                  off={state.muted || meServerMuted}
                />
              </button>
              <button
                className="hov-bright"
                onClick={actions.toggleDeafen}
                title="Áudio"
                style={controlStyle(state.deafened, redBg, C.red)}
              >
                <HeadphonesIcon
                  size={18}
                  color={state.deafened ? C.red : offFg}
                  off={state.deafened}
                />
              </button>
              <button
                className={canShare ? "hov-bright" : undefined}
                onClick={actions.toggleShare}
                disabled={!canShare}
                title={
                  canShare
                    ? "Compartilhar tela"
                    : "Você não tem permissão para compartilhar a tela."
                }
                style={{
                  ...controlStyle(state.sharing, accentBg, accent),
                  cursor: canShare ? "pointer" : "not-allowed",
                  opacity: canShare ? 1 : 0.45,
                }}
              >
                <ScreenIcon size={18} color={state.sharing ? accent : offFg} />
              </button>
              <button
                className="hov-bright"
                onClick={actions.toggleCam}
                title="Câmera"
                style={controlStyle(state.cam, accentBg, accent)}
              >
                <CamIcon size={18} color={state.cam ? accent : offFg} />
              </button>
              <span style={{ width: 1, height: 24, background: C.lineStrong }} />
              <button
                className="hov-bright-110"
                onClick={actions.leaveVoice}
                title="Desconectar"
                style={{
                  height: 42,
                  padding: "0 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  border: 0,
                  borderRadius: 999,
                  cursor: "pointer",
                  background: C.red,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <PowerIcon size={16} color="#fff" />
                Sair
              </button>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: 8,
                border: `1px solid ${C.lineStrong}`,
                borderRadius: 999,
                background: C.raised,
              }}
            >
              <button
                className={canVoice ? "hov-bright-108" : undefined}
                onClick={() => actions.joinVoice(viewed.id)}
                disabled={!canVoice}
                title={
                  canVoice
                    ? `Conectar em ${viewed.name}`
                    : "Você não tem permissão para conectar em voz."
                }
                style={{
                  height: 42,
                  padding: "0 20px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  border: 0,
                  borderRadius: 999,
                  cursor: canVoice ? "pointer" : "not-allowed",
                  background: "var(--ac,#1F4FD8)",
                  color: C.ink,
                  fontSize: 13,
                  fontWeight: 700,
                  opacity: canVoice ? 1 : 0.45,
                }}
              >
                <SpeakerIcon size={16} color={C.ink} />
                Entrar em {viewed.name}
              </button>
            </div>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div style={pillStyle}>
              <span style={pillLabelStyle}>QUALIDADE</span>
              <select
                value={state.shareQuality}
                onChange={onQuality}
                title="Qualidade da transmissão de tela"
                style={selectStyle}
              >
                {QUALITY_ORDER.map(id => (
                  <option key={id} value={id}>
                    {QUALITY_PRESETS[id].label}
                  </option>
                ))}
              </select>
            </div>

            <div style={pillStyle}>
              <span style={pillLabelStyle}>CONTEÚDO</span>
              <div
                style={{
                  display: "flex",
                  gap: 2,
                  padding: 3,
                  borderRadius: 999,
                  background: C.field,
                }}
              >
                {CONTENT_MODES.map(mode => {
                  const on = state.contentHint === mode.id;
                  return (
                    <button
                      key={mode.id}
                      className={on ? undefined : "hov-bg-26"}
                      onClick={() => actions.setContentHint(mode.id)}
                      title={mode.title}
                      style={{
                        height: 22,
                        padding: "0 12px",
                        border: 0,
                        borderRadius: 999,
                        cursor: "pointer",
                        background: on ? C.selected : "transparent",
                        color: on ? C.text : C.textMuted,
                        fontSize: 11.5,
                        fontWeight: 600,
                      }}
                    >
                      {mode.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {connected && (
              <Fragment>
                <DeviceSelect
                  label="ENTRADA"
                  devices={inputs}
                  value={inputValue}
                  fallbackName="Microfone"
                  disabled={false}
                  title="Microfone usado na sala"
                  onSelect={actions.setInputDevice}
                />
                <DeviceSelect
                  label="SAÍDA"
                  devices={outputs}
                  value={outputValue}
                  fallbackName="Saída"
                  disabled={!canOutput}
                  title={canOutput ? "Dispositivo que toca o áudio da sala" : SINK_UNSUPPORTED}
                  onSelect={onOutputDevice}
                />
                <div style={pillStyle}>
                  <span style={pillLabelStyle}>MICROFONE</span>
                  <div
                    style={{
                      display: "flex",
                      gap: 2,
                      padding: 3,
                      borderRadius: 999,
                      background: C.field,
                    }}
                  >
                    {TALK_MODES.map(mode => {
                      const on = state.pushToTalk === mode.on;
                      return (
                        <button
                          key={mode.label}
                          className={on ? undefined : "hov-bg-26"}
                          onClick={() => actions.setPushToTalk(mode.on)}
                          title={mode.title}
                          style={{
                            height: 22,
                            padding: "0 12px",
                            border: 0,
                            borderRadius: 999,
                            cursor: "pointer",
                            background: on ? C.selected : "transparent",
                            color: on ? C.text : C.textMuted,
                            fontSize: 11.5,
                            fontWeight: 600,
                          }}
                        >
                          {mode.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div style={pillStyle}>
                  <span style={pillLabelStyle}>RUÍDO</span>
                  <button
                    type="button"
                    onClick={() => actions.setNoiseSuppression(!state.noiseSuppression)}
                    title="Supressão de ruído do microfone por IA — reduz teclado, ventoinha e barulho de fundo, tipo o Krisp do Discord"
                    style={{
                      position: "relative",
                      width: 34,
                      height: 19,
                      border: 0,
                      borderRadius: 999,
                      cursor: "pointer",
                      background: state.noiseSuppression ? accent : C.track,
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        top: 2,
                        left: state.noiseSuppression ? 17 : 2,
                        width: 15,
                        height: 15,
                        borderRadius: "50%",
                        background: "#fff",
                        transition: "left .15s",
                      }}
                    />
                  </button>
                </div>
              </Fragment>
            )}
          </div>

          {connected && state.pushToTalk && (
            <span
              style={{
                fontFamily: FONT_MONO,
                fontSize: 10,
                letterSpacing: ".1em",
                color: C.textLabel,
              }}
            >
              SEGURE A BARRA DE ESPAÇO PARA FALAR
            </span>
          )}

          {connected && !canOutput && (
            <span style={{ fontSize: 11.5, color: C.textLabel, textAlign: "center" }}>
              {SINK_UNSUPPORTED}
            </span>
          )}

          {connected && sinkError && (
            <span style={{ fontSize: 11.5, color: C.red, textAlign: "center" }}>{sinkError}</span>
          )}
        </div>
      </div>

      {audioPeers.map(item => {
        const media = mediaOf(item.peerId);
        const volume = volumeOf(item.userId);
        return (
          <Fragment key={`audio:${item.peerId}`}>
            {media.mic && (
              <HiddenAudio
                stream={media.mic}
                deafened={state.deafened || webLocalMuteForCapture}
                volume={volume}
                sinkId={outputValue}
                onSinkError={handleSinkError}
              />
            )}
            {media.screen && (
              <HiddenAudio
                stream={media.screen}
                deafened={state.deafened || webLocalMuteForCapture}
                volume={volume}
                sinkId={outputValue}
                onSinkError={handleSinkError}
              />
            )}
          </Fragment>
        );
      })}

      {focusEntry && (
        <div
          ref={overlayRef}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 70,
            background: "#0d0d0d",
            display: "flex",
            flexDirection: "column",
            animation: "fadein 150ms ease",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 14,
              right: 16,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: 6,
              border: `1px solid ${C.lineStrong}`,
              borderRadius: 999,
              background: "rgba(20,20,20,.82)",
            }}
          >
            <button
              className="hov-bg-26"
              onClick={leaveFullscreen}
              title="Sair da tela cheia (Esc)"
              style={{
                height: 30,
                padding: "0 14px",
                border: 0,
                borderRadius: 999,
                cursor: "pointer",
                background: "transparent",
                color: C.textSoft,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Sair da tela cheia
            </button>
            <span style={{ width: 1, height: 18, background: C.lineStrong }} />
            <button
              className="hov-bg-26"
              onClick={closeFocus}
              title="Voltar para a grade da sala"
              style={{
                height: 30,
                padding: "0 14px",
                border: 0,
                borderRadius: 999,
                cursor: "pointer",
                background: "transparent",
                color: C.textSoft,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Minimizar
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
            <VideoTile
              stream={focusStream}
              label={focusIsSelf ? "Sua transmissão" : `Tela de ${nameOf(focusEntry.userId)}`}
              kind="screen"
              stats={state.stats[focusEntry.peerId]?.screen}
              live
              muted
              focused
              emptyState={
                focusIsSelf && !previaPropria ? <SelfShareNotice accent={accent} /> : undefined
              }
            />
          </div>
        </div>
      )}

      <ShareScreenModal />
    </div>
  );
}
